import {
  type RemoteThreadListAdapter,
  ThreadMessage,
  RuntimeAdapterProvider,
  ThreadHistoryAdapter,
  useAui,
  type AssistantClient,
} from "@assistant-ui/react";
import { createAssistantStream, type AssistantStream } from "assistant-stream";
import {
  GramChatOverview,
  GramChat,
  GramChatMessage,
  convertGramMessagesToExported,
  convertGramMessagesToUIMessages,
} from "@/elements/lib/messageConverter";
import { sleep } from "@/lib/utils";
import { trackError } from "@/elements/lib/errorTracking";
import {
  ThreadMetaContext,
  type ThreadMeta,
} from "@/elements/contexts/ThreadMetaContext";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";

/**
 * Prefix used by assistant-ui for local thread IDs that haven't been persisted yet.
 * This is an internal implementation detail of assistant-ui's RemoteThreadListThreadListRuntimeCore.
 * If the library changes this prefix, we only need to update it here.
 */
const LOCAL_THREAD_ID_PREFIX = "__LOCALID_";

/**
 * Checks if a thread ID is a local (unpersisted) thread ID.
 * Local IDs are generated by assistant-ui before the thread is initialized with a remote ID.
 */
export function isLocalThreadId(threadId: string | null | undefined): boolean {
  return !!threadId?.startsWith(LOCAL_THREAD_ID_PREFIX);
}

/**
 * Polls the shared local→remote id map until the transport assigns an id for
 * `threadId`, or a deadline passes. Used in deferred-minting mode so a new
 * thread adopts the backend-minted chat id instead of a client-generated one.
 * The timeout is generous to tolerate cold serverless boots on the first send.
 */
async function waitForMappedId(
  map: Map<string, string> | undefined,
  threadId: string,
  timeoutMs = 30_000,
  intervalMs = 50,
): Promise<string | undefined> {
  if (!map) return undefined;
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const id = map.get(threadId);
    if (id) return id;
    if (Date.now() >= deadline) return undefined;
    await sleep(intervalMs);
  }
}

/**
 * Transforms or drops a persisted chat message before it is rendered from
 * history. Return the (possibly rewritten) message, or `null` to omit it.
 */
export type ChatMessageTransform = (
  message: GramChatMessage,
) => GramChatMessage | null;

export interface ThreadListAdapterOptions {
  apiUrl: string;
  headers: Record<string, string>;
  /**
   * Async header resolution that waits for auth to settle (e.g. the session
   * token fetch) before returning. When provided, every adapter request uses
   * this instead of the `headers` snapshot — which lets the runtime mount
   * before auth resolves: the initial `list()` fires immediately on bind and
   * would otherwise go out with incomplete headers.
   */
  getHeaders?: () => Promise<Record<string, string>>;
  /** Map to translate local thread IDs to UUIDs (shared with transport) */
  localIdToUuidMap?: Map<string, string>;
  /**
   * Extra query parameters forwarded to `chat.list` to filter which threads are
   * listed. Opaque to the adapter — the consumer chooses the keys.
   */
  threadListFilters?: Record<string, string>;
  /**
   * Don't client-mint a chat id for a brand-new thread. When true, `initialize`
   * waits for the transport to assign the id (via the shared `localIdToUuidMap`,
   * e.g. a server-minted id reported through the transport context's
   * `adoptChatId` bind closure) instead of generating one with
   * `crypto.randomUUID()`. Use this when the backend owns chat-id creation.
   */
  deferThreadIdMinting?: boolean;
  /**
   * Optional hook to transform or drop each persisted message before it is
   * converted for rendering. Return a message to render it (possibly rewritten),
   * or `null` to omit it. Keeps product-specific transcript conventions out of
   * the library — see {@link HistoryConfig.transformChatMessage}.
   */
  transformChatMessage?: ChatMessageTransform;
  /** See {@link HistoryConfig.resolveCreator}. */
  resolveCreator?: (chat: {
    userId?: string;
    externalUserId?: string;
  }) => { name?: string; email: string; photoUrl?: string } | undefined;
  /** See {@link HistoryConfig.isOwnChat}. */
  isOwnChat?: (chat: { userId?: string; externalUserId?: string }) => boolean;
}

interface ListChatsResponse {
  chats: GramChatOverview[];
}

/**
 * Reads a chat's creation timestamp from the `chat.list` payload. The wire
 * format is snake_case (`created_at`) — the hand-written GramChatOverview type
 * declares camelCase, which never matched the JSON — so check both and return
 * an ISO string, or undefined when absent.
 */
function readChatCreatedAt(chat: GramChatOverview): string | undefined {
  const raw = chat as unknown as Record<string, unknown>;
  const value = raw["created_at"] ?? raw["createdAt"];
  return typeof value === "string" ? value : undefined;
}

/** Reads a chat's creator (Speakeasy user id) from the `chat.list` payload. */
function readChatUserId(chat: GramChatOverview): string | undefined {
  const raw = chat as unknown as Record<string, unknown>;
  const value = raw["user_id"] ?? raw["userId"];
  return typeof value === "string" && value !== "" ? value : undefined;
}

/** Reads a chat's external user id from the `chat.list` payload. */
function readChatExternalUserId(chat: GramChatOverview): string | undefined {
  const raw = chat as unknown as Record<string, unknown>;
  const value = raw["external_user_id"] ?? raw["externalUserId"];
  return typeof value === "string" && value !== "" ? value : undefined;
}

/**
 * Resolves request headers from the live adapter options: the async
 * `getHeaders` (which waits for auth to settle) when provided, otherwise the
 * static `headers` snapshot.
 */
async function resolveAdapterHeaders(
  options: ThreadListAdapterOptions,
): Promise<Record<string, string>> {
  return options.getHeaders ? options.getHeaders() : options.headers;
}

// chat.load paginates by seq keyset and returns only the newest page by default.
// assistant-ui's history adapter is one-shot (it imports the whole thread at
// once), so page backwards to the start and return the chat with every message
// in ascending order. Without this a long thread would render only its newest
// page with no way to reach the rest.
const FULL_LOAD_PAGE_SIZE = 200;

async function loadFullChat(
  apiUrl: string,
  chatId: string,
  headers: Record<string, string>,
): Promise<GramChat | null> {
  let beforeSeq: number | undefined;
  let all: GramChatMessage[] = [];
  let base: GramChat | null = null;

  for (;;) {
    const cursor = beforeSeq !== undefined ? `&before_seq=${beforeSeq}` : "";
    const response = await fetch(
      `${apiUrl}/rpc/chat.load?id=${encodeURIComponent(chatId)}&limit=${FULL_LOAD_PAGE_SIZE}${cursor}`,
      { headers },
    );
    if (!response.ok) {
      // First page failed → nothing to show. A later page failing means we'd
      // silently truncate the thread, so log loudly rather than passing partial
      // history off as complete.
      if (!base) {
        console.error("Failed to load chat:", response.status);
        return null;
      }
      console.error(
        `Failed to load full chat history (status ${response.status}); ` +
          `returning ${all.length} of ${base.numMessages ?? "?"} messages — transcript is truncated.`,
      );
      return { ...base, messages: all };
    }
    const page = (await response.json()) as GramChat;
    if (!base) base = page;
    // Each page is ascending; older pages prepend.
    all = [...page.messages, ...all];
    const oldest = page.messages[0];
    if (!page.has_more_before || !oldest || oldest.seq === undefined) break;
    beforeSeq = oldest.seq;
  }

  return base ? { ...base, messages: all } : null;
}

/**
 * Thread history adapter that loads messages from Speakeasy API.
 * Note: We use `as ThreadHistoryAdapter` cast because the withFormat generic
 * signature doesn't match our concrete implementation, but it works at runtime.
 */
class GramThreadHistoryAdapter {
  private apiUrl: string;
  private getHeaders: () => Promise<Record<string, string>>;
  private store: AssistantClient;
  // Read lazily rather than captured: the adapter is constructed once, but the
  // consumer may swap `transformChatMessage` across renders, so resolve it from
  // the live options on every load instead of snapshotting it here.
  private getTransformChatMessage?: () => ChatMessageTransform | undefined;

  constructor(
    apiUrl: string,
    getHeaders: () => Promise<Record<string, string>>,
    store: AssistantClient,
    getTransformChatMessage?: () => ChatMessageTransform | undefined,
  ) {
    this.apiUrl = apiUrl;
    this.getHeaders = getHeaders;
    this.store = store;
    this.getTransformChatMessage = getTransformChatMessage;
  }

  /**
   * Applies the consumer-supplied `transformChatMessage` hook to a loaded
   * transcript: rewrites each message and drops any the hook returns `null` for.
   * Without a hook configured the messages pass through untouched.
   */
  private applyTransform(messages: GramChatMessage[]): GramChatMessage[] {
    const transform = this.getTransformChatMessage?.();
    if (!transform) {
      return messages;
    }
    const result: GramChatMessage[] = [];
    for (const message of messages) {
      const transformed = transform(message);
      if (transformed) {
        result.push(transformed);
      }
    }
    return result;
  }

  async load() {
    const remoteId = this.store.threadListItem().getState().remoteId;
    if (!remoteId) {
      return { messages: [], headId: null };
    }

    try {
      const chat = await loadFullChat(
        this.apiUrl,
        remoteId,
        await this.getHeaders(),
      );
      if (!chat) {
        console.error("Failed to load chat");
        return { messages: [], headId: null };
      }
      return convertGramMessagesToExported(
        this.applyTransform(chat.messages),
        this.apiUrl,
      );
    } catch (error) {
      console.error("Error loading chat:", error);
      return { messages: [], headId: null };
    }
  }

  async append() {
    // No-op: Speakeasy persists messages server-side during streaming.
  }

  // Required by ThreadHistoryAdapter - wraps adapter with format conversion.
  // The _formatAdapter param is part of the interface but unused since we handle conversion ourselves.
  // Using arrow functions to capture `this` lexically.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  withFormat(_formatAdapter: unknown) {
    return {
      load: async () => {
        const remoteId = this.store.threadListItem().getState().remoteId;
        if (!remoteId) {
          return { messages: [], headId: null };
        }

        const chat = await loadFullChat(
          this.apiUrl,
          remoteId,
          await this.getHeaders(),
        );
        if (!chat) {
          console.error("Failed to load chat (withFormat)");
          return { messages: [], headId: null };
        }
        return convertGramMessagesToUIMessages(
          this.applyTransform(chat.messages),
          this.apiUrl,
        );

        // // Filter out system messages (assistant-ui doesn't support them in the import path)
        // const filteredMessages = chat.messages.filter(
        //   (msg) => msg.role !== 'system'
        // )

        // if (filteredMessages.length === 0) {
        //   return { messages: [], headId: null }
        // }

        // // Convert to the format expected by useExternalHistory
        // // It expects UIMessage format with role and parts array
        // let prevId: string | null = null
        // const messages = filteredMessages.map((msg, index) => {
        //   // Generate a fallback ID if missing (required by assistant-ui's MessageRepository)
        //   const messageId = msg.id || `fallback-${index}-${Date.now()}`
        //   const uiMessage = {
        //     parentId: prevId,
        //     message: {
        //       id: messageId,
        //       role: msg.role as 'user' | 'assistant',
        //       parts: [{ type: 'text' as const, text: msg.content || '' }],
        //       createdAt: msg.createdAt ? new Date(msg.createdAt) : new Date(),
        //     },
        //   }
        //   prevId = messageId
        //   return uiMessage
        // })

        // return {
        //   headId: prevId,
        //   messages,
        // }
      },
      append: async () => {
        // No-op
      },
    };
  }
}

/**
 * Hook to create a Speakeasy thread history adapter.
 */
function useGramThreadHistoryAdapter(
  optionsRef: React.RefObject<ThreadListAdapterOptions>,
): ThreadHistoryAdapter {
  const store = useAui();
  const [adapter] = useState(
    () =>
      new GramThreadHistoryAdapter(
        optionsRef.current.apiUrl,
        () => resolveAdapterHeaders(optionsRef.current),
        store,
        () => optionsRef.current.transformChatMessage,
      ),
  );
  // Cast to ThreadHistoryAdapter - the withFormat generic doesn't match but works at runtime
  return adapter as unknown as ThreadHistoryAdapter;
}

/**
 * Hook that creates a RemoteThreadListAdapter for the Speakeasy API.
 * This properly handles React component identity for the Provider.
 */
export function useGramThreadListAdapter(
  options: ThreadListAdapterOptions,
): RemoteThreadListAdapter {
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Side channel for per-chat metadata (creation date) that assistant-ui's
  // RemoteThreadMetadata can't carry. `list()` writes a fresh object into the
  // ref and bumps the counter so the provider passes a new context value and
  // ThreadListItem re-renders with the dates. A ref (not state) holds the data
  // so the stable `list()` closure can write it without a re-created identity.
  const metaRef = useRef<Record<string, ThreadMeta>>({});
  const [, bumpMeta] = useState(0);

  // Create stable Provider component using useCallback
  const unstable_Provider = useCallback(function GramHistoryProvider({
    children,
  }: PropsWithChildren) {
    const history = useGramThreadHistoryAdapter(optionsRef);
    const adapters = useMemo(() => ({ history }), [history]);
    return (
      <ThreadMetaContext.Provider value={metaRef.current}>
        <RuntimeAdapterProvider adapters={adapters}>
          {children}
        </RuntimeAdapterProvider>
      </ThreadMetaContext.Provider>
    );
  }, []);

  // Return adapter with stable methods
  return useMemo(
    () => ({
      unstable_Provider,

      async list() {
        try {
          const { apiUrl, threadListFilters } = optionsRef.current;
          const headers = await resolveAdapterHeaders(optionsRef.current);
          const qs = threadListFilters
            ? new URLSearchParams(threadListFilters).toString()
            : "";
          const listUrl = qs
            ? `${apiUrl}/rpc/chat.list?${qs}`
            : `${apiUrl}/rpc/chat.list`;
          const response = await fetch(listUrl, { headers });

          if (!response.ok) {
            console.error("Failed to list chats:", response.status);
            return { threads: [] };
          }

          const data = (await response.json()) as ListChatsResponse;

          // Stash creation dates + resolved owner in the side channel before
          // returning the assistant-ui metadata (which can't carry them) —
          // keyed by chat id, the same value used for remoteId/externalId
          // below. `resolveCreator` is consumer-supplied and synchronous (the
          // consumer already holds whatever identity data it needs), so no
          // extra request goes out here.
          const { resolveCreator, isOwnChat } = optionsRef.current;
          const nextMeta: Record<string, ThreadMeta> = { ...metaRef.current };
          for (const chat of data.chats) {
            const chatIdentity = {
              userId: readChatUserId(chat),
              externalUserId: readChatExternalUserId(chat),
            };
            nextMeta[chat.id] = {
              createdAt: readChatCreatedAt(chat),
              owner: resolveCreator?.(chatIdentity),
              readOnly: isOwnChat ? !isOwnChat(chatIdentity) : false,
            };
          }
          metaRef.current = nextMeta;
          bumpMeta((n) => n + 1);
          return {
            threads: data.chats.map((chat) => ({
              remoteId: chat.id,
              externalId: chat.id,
              status: "regular" as const,
              title: chat.title || "New Chat",
            })),
          };
        } catch (error) {
          console.error("Error listing chats:", error);
          return { threads: [] };
        }
      },

      async initialize(threadId: string) {
        // For new threads (local IDs), check if sendMessages already created a UUID
        if (isLocalThreadId(threadId)) {
          // Check if transport already generated a UUID for this local ID
          const existingUuid =
            optionsRef.current.localIdToUuidMap?.get(threadId);
          if (existingUuid) {
            return {
              remoteId: existingUuid,
              externalId: existingUuid,
            };
          }
          // When the backend owns chat-id creation, don't client-mint: wait for
          // the transport to report the server-minted id (it assigns it during
          // the first send via the adoptChatId bind closure, populating the
          // shared map — the same path the built-in transport uses).
          if (optionsRef.current.deferThreadIdMinting) {
            const mappedUuid = await waitForMappedId(
              optionsRef.current.localIdToUuidMap,
              threadId,
            );
            if (mappedUuid) {
              return {
                remoteId: mappedUuid,
                externalId: mappedUuid,
              };
            }
            // Falling through to crypto.randomUUID() here would defeat deferred
            // minting: the client id would race the server-minted one reported
            // later via the adoptChatId bind closure, leaving runtime state
            // and the local→remote map disagreeing. Surface the failure to
            // the user instead.
            throw new Error(
              "Backend did not mint a chat id in time — first send may have failed or is still in flight. Retry the send.",
            );
          }
          // Otherwise generate a new one and store it
          const uuid = crypto.randomUUID();
          optionsRef.current.localIdToUuidMap?.set(threadId, uuid);
          return {
            remoteId: uuid,
            externalId: uuid,
          };
        }
        // For existing threads, use the ID as-is
        return {
          remoteId: threadId,
          externalId: threadId,
        };
      },

      async rename(remoteId: string, newTitle: string) {
        // Brand-new threads only have a local id until the first message
        // persists them server-side; there's nothing to rename yet.
        if (!remoteId || isLocalThreadId(remoteId)) {
          return;
        }

        // Reuses chat.generateTitle: passing `title` sets a manual title that
        // auto-generation won't overwrite (empty string resets to auto-naming).
        //
        // On failure we track AND rethrow: assistant-ui applies the rename
        // optimistically and only rolls the title back if this promise rejects.
        // Swallowing the error would leave a title showing that never persisted.
        try {
          const response = await fetch(
            `${optionsRef.current.apiUrl}/rpc/chat.generateTitle`,
            {
              method: "POST",
              headers: {
                ...(await resolveAdapterHeaders(optionsRef.current)),
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ id: remoteId, title: newTitle }),
            },
          );

          if (!response.ok) {
            throw new Error(`Failed to rename chat: ${response.status}`);
          }
        } catch (error) {
          trackError(error, {
            source: "custom",
            context: "useGramThreadListAdapter.rename",
          });
          throw error;
        }
      },

      async archive() {
        // No-op
      },

      async unarchive() {
        // No-op
      },

      async delete() {
        // No-op
      },

      async generateTitle(
        remoteId: string,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _messages: readonly ThreadMessage[],
      ): Promise<AssistantStream> {
        // Skip if this is a local ID that hasn't been persisted yet
        if (!remoteId || isLocalThreadId(remoteId)) {
          return createAssistantStream((controller) => {
            controller.close();
          });
        }

        // Title generation happens async server-side via Temporal after first completion.
        // This delay allows the OpenRouter LLM call to complete before we fetch the title.
        const TITLE_GENERATION_DELAY_MS = 2000;
        await new Promise((r) => {
          setTimeout(r, TITLE_GENERATION_DELAY_MS);
        });

        try {
          // TODO: rename generateTitle endpoint to getTitle
          const response = await fetch(
            `${optionsRef.current.apiUrl}/rpc/chat.generateTitle`,
            {
              method: "POST",
              headers: {
                ...(await resolveAdapterHeaders(optionsRef.current)),
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ id: remoteId }),
            },
          );

          if (response.ok) {
            const result = (await response.json()) as { title: string };
            const title = result.title;

            if (title) {
              return createAssistantStream((controller) => {
                controller.appendText(title);
                controller.close();
              });
            }
          }
        } catch (error) {
          console.error("Error fetching title:", error);
        }

        // If title fetch failed or got placeholder, return empty stream (keeps existing title)
        return createAssistantStream((controller) => {
          controller.close();
        });
      },

      async fetch(threadId: string) {
        // Skip if this is a local ID that hasn't been persisted yet
        if (!threadId || isLocalThreadId(threadId)) {
          return {
            remoteId: threadId,
            status: "regular" as const,
          };
        }

        try {
          // Only chat metadata (id/title) is needed here, so request the
          // smallest page instead of a full message page.
          const response = await fetch(
            `${optionsRef.current.apiUrl}/rpc/chat.load?id=${encodeURIComponent(threadId)}&limit=1`,
            {
              headers: await resolveAdapterHeaders(optionsRef.current),
            },
          );

          if (!response.ok) {
            console.error("Failed to fetch thread:", response.status);
            return {
              remoteId: threadId,
              status: "regular" as const,
            };
          }

          const chat = await response.json();
          return {
            remoteId: chat.id,
            externalId: chat.id,
            status: "regular" as const,
            title: chat.title || "New Chat",
          };
        } catch (error) {
          console.error("Error fetching thread:", error);
          return {
            remoteId: threadId,
            status: "regular" as const,
          };
        }
      },
    }),
    [unstable_Provider],
  );
}
